<template>
  <div class="max-w-screen-lg">
    <div class="flex flex-col gap-y-8">
      <!-- Hero Section -->
      <div class="flex relative bg-blue-600 py-6 px-10 gap-3 text-white rounded-lg">
        <div>
          <div class="flex flex-row items-center gap-2">
            <Icon 
              name="star" 
              size="12" 
              class="hidden md:block -ml-4 mr-2 opacity-50 transform -rotate-12 self-center text-white"
            />
            <h2 class="text-lg leading-snug font-semibold">
              Refer Sub-Affiliates to receive <span class="font-extrabold">10%</span> of their commissions on the first 6 months.
            </h2>
          </div>
        </div>
      </div>

      <!-- Referral Link Section -->
      <div class="text-gray-800 flex flex-col gap-y-4">
        <p>Here is your referral link:</p>
        <div class="max-w-screen-sm">
          <YInput
            v-if="me && me.id"
            :value="referralCode"
            id="referralCode"
            label="Your Referral Code"
            type="text"
            readonly
            class="text-black"
          />
          <div
            @click="copyToClipboard"
            class="mt-2 ml-2 text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
          >
            Copy Link
          </div>
        </div>
      </div>
    </div>

    <!-- Expandable Overview Section -->
    <div class="mt-8">
      <Expandable
        title="All In Referral Program Overview"
        :initial-state="false"
        clickable-class="flex w-full justify-between rounded-lg bg-gray-100 px-4 py-8 text-left hover:bg-gray-200 focus:outline-none"
      >
        <template v-slot:title>
          <div class="flex flex-col gap-2 text-xs">
            <p class="text-lg font-bold">All In Referral Program Overview</p>
          </div>
        </template>
        
        <div class="pb-2 mt-4 bg-gray-50 rounded-md">
          <div class="eligibility p-4 lg:p-8">
            <div class="mb-8">
              <h2 class="text-lg font-bold mb-4">Eligibility Criteria</h2>
              <ul class="list-decimal ml-8">
                <li class="mb-2">
                  <p class="mb-2">The referred sub-affiliate must:</p>
                  <ul class="list-disc ml-4 mb-4">
                    <li>
                      Be a new member to the network (no existing accounts).
                    </li>
                    <li>
                      Actively promote campaigns and generate valid conversions.
                    </li>
                  </ul>
                </li>
                <li class="mb-2">
                  <p class="mb-2">
                    The conversions must be genuine, non-fraudulent, and
                    meet the specific campaign terms.
                  </p>
                </li>
              </ul>
            </div>
            
            <div class="mb-8">
              <h2 class="text-lg font-bold mb-4">Payment Terms</h2>
              <ol class="list-decimal ml-8">
                <li class="mb-2">
                  <p class="mb-2">
                    The 10% revenue share payments will be calculated and
                    credited monthly, based on the referred sub-affiliate's
                    performance during the qualifying 6-month period.
                  </p>
                </li>
                <li class="mb-2">
                  <p class="mb-2">
                    Payments will be credited to the referring affiliate's account 
                    within 15 days from the end of the month.
                  </p>
                </li>
              </ol>
            </div>
            
            <div class="mb-8">
              <h2 class="text-lg font-bold mb-4">Important Notes</h2>
              <ol class="list-decimal ml-8">
                <li class="mb-2">
                  <p class="font-semibold mb-2">Fraud Prevention:</p>
                  <ul class="list-disc ml-4 mb-4">
                    <li>
                      All referrals are subject to review. Fraudulent activity,
                      including self-referrals or artificial traffic, will
                      result in disqualification from the program.
                    </li>
                  </ul>
                </li>
                <li class="mb-2">
                  <p class="font-semibold mb-2">Multiple Referrals:</p>
                  <ul class="list-disc ml-4 mb-4">
                    <li>
                      There is no limit to the number of sub-affiliates you can
                      refer.
                    </li>
                  </ul>
                </li>
                <li class="mb-2">
                  <p class="font-semibold mb-2">Program Modifications:</p>
                  <ul class="list-disc ml-4 mb-4">
                    <li>
                      YTZ reserves the right to modify or
                      terminate this program at any time with prior notice to
                      participants.
                    </li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </Expandable>
    </div>

    <!-- Table Section -->
    <div class="table-container mt-12">
      <h2 class="text-lg font-semibold mb-2">Sub-Affiliate Commissions</h2>      
      <YTable 
        :data="subAffiliateCommissions" 
        :loading="reportLoading"
        class="table table-sm w-full"
        :empty-text="!reportLoading ? 'No sub-affiliate commission data available.' : 'Loading...'"
      >
        <template v-slot:header-row>
          <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
            Referred Affiliate ID
          </th>
          <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
            Month
          </th>
          <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
            Commission
          </th>
        </template>
        <template v-slot:row="{ rowData }">
          <td class="px-6 py-4 whitespace-no-wrap border-b border-gray-200">
            {{ rowData.id }}
          </td>
          <td class="px-6 py-4 whitespace-no-wrap border-b border-gray-200">
            {{ rowData.month }}
          </td>
          <td class="px-6 py-4 whitespace-no-wrap border-b border-gray-200">
            {{ $formatMoney(rowData.commission) }}
          </td>
        </template>
      </YTable>
      
      <!-- <div v-if="!reportLoading && subAffiliateCommissions.length === 0" class="text-center py-8 text-gray-500">
        No sub-affiliate commission data available.
      </div> -->
    </div>
  </div>
</template>

<script>
import ME_QUERY from '@/graphql/User/MeQuery.gql'
import REFERRAL_REVENUE_QUERY from '@/graphql/Report/ReferralRevenueQuery.gql'

// TODO: MAKE Referral Code more visible (looks grayed out)
export default {
  name: 'Subaffiliates',
  apollo: {
    me: {
      query: ME_QUERY,
      result({ data }) {
        if (data && data.me) {
          this.user = data.me
        }
      },
      error(error) {
        console.error('Error loading user data:', error)
      }
    }
  },
  data() {
    return {
      me: null,
      user: null,
      reportData: null,
      reportLoading: false,
      tableSortByValue: [
        {
          field: 'month',
          sortField: 'month',
          direction: 'desc'
        }
      ]
    }
  },
  computed: {
    referralCode() {
      if (!this.me || !this.me.id) return ''
      return `https://ytz.com/signup/?ref=${this.me.id}`
    },
    yesterdaysDate() {
      return this.$dateTime.local().minus({ days: 1 }).toFormat('MMMM d, yyyy')
    },
    reportVariables() {
      const now = this.$dateTime.local();
      
      // Skip query if current date is before July 2025
      if (now.year < 2025 || (now.year === 2025 && now.month < 7)) {
        return null; // or return early/skip query however your system handles it
      }
      
      return {
        filters: {
          dateRange: [
            this.$dateTime.local()
              .set({ year: 2025, month: 7, day: 1 }) // July 1st, 2025
              .startOf('day')
              .toFormat('yyyy-LL-dd'),
            this.$dateTime.local().endOf('month').toFormat('yyyy-LL-dd')
          ]
        },
        fields: ['month', 'referralPayoutFrom', 'referralPayout'],
        sortOrder: this.tableSortByValue
      }
    },
    subAffiliateCommissions() {
      if (!this.reportData || !this.reportData.data) {
        return []
      }
      
      return this.reportData.data.map(item => ({
        id: this.formatReferralSource(item.referralPayoutFrom),
        month: item.month,
        commission: item.referralPayout
      }))
    }
  },
  watch: {
    me: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadReferralReport()
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadReferralReport() {
      if (!this.me || !this.me.id) return
      
      this.reportLoading = true
      try {
        const result = await this.$apollo.query({
          query: REFERRAL_REVENUE_QUERY,
          variables: this.reportVariables,
          fetchPolicy: 'cache-first'
        })
        
        if (result.data && result.data.report) {
          this.reportData = result.data.report
        }
      } catch (error) {
        console.error('Error loading referral revenue report:', error)
      } finally {
        this.reportLoading = false
      }
    },
    formatReferralSource(referralPayoutFrom) {
      // results should be in a string format like 1234 - allIn_email@gmail.com
      // we want to strip the "{id} - allIn_" from the start of the string
      // we want to return the rest of the email after allIn_
      if (!referralPayoutFrom || typeof referralPayoutFrom !== 'string') {
        return referralPayoutFrom
      }
      
      const parts = referralPayoutFrom.split('allIn_')
      return parts.length > 1 ? parts[1] : referralPayoutFrom
    },
    copyToClipboard() {
      if (!this.referralCode) return
      
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.referralCode).then(() => {
          this.$toastr.s('Referral link copied to clipboard!', 'Success')
        }).catch(err => {
          console.error('Failed to copy to clipboard:', err)
          this.fallbackCopyToClipboard()
        })
      } else {
        this.fallbackCopyToClipboard()
      }
    },
    fallbackCopyToClipboard() {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea')
      textArea.value = this.referralCode
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        // @ts-ignore - execCommand is deprecated but necessary for fallback
        const successful = document.execCommand('copy')
        if (successful) {
          this.$toastr.s('Referral link copied to clipboard!', 'Success')
        } else {
          this.$toastr.e('Failed to copy referral link', 'Error')
        }
      } catch (err) {
        console.error('Fallback copy failed:', err)
        this.$toastr.e('Failed to copy referral link', 'Error')
      }
      document.body.removeChild(textArea)
    }
  }
}
</script>